import { useState } from 'react'
import { api } from '../lib/api'
import { formatTime } from '../lib/format'
import type { BabyRecord, RecordType } from '../lib/types'
import { DIAPER_COLORS, DIAPER_TEXTURES } from '../lib/types'
import { useToast } from './Toast'
import { stablePhotoUrl } from './RecordsList'

interface Props {
  record: BabyRecord
  onClose: () => void
  onDeleted: () => void
  aiVisionEnabled: boolean
}

const ICONS: Record<RecordType, { emoji: string; label: string }> = {
  pee: { emoji: '💧', label: '嘘嘘' },
  poop: { emoji: '💩', label: '臭臭' },
  mixed: { emoji: '✨', label: '混合' },
}

interface AiTags {
  color?: string
  texture?: string
  tags?: string[]
  summary?: string
}

export default function RecordDetail({ record, onClose, onDeleted, aiVisionEnabled }: Props) {
  const toast = useToast()
  const icon = ICONS[record.type]
  const [aiRunning, setAiRunning] = useState(false)
  const [aiTags, setAiTags] = useState<AiTags | null>(() => {
    if (!record.ai_summary) return null
    try {
      return JSON.parse(record.ai_summary) as AiTags
    } catch {
      return null
    }
  })

  async function runAi() {
    if (!record.photo_id) {
      toast.show('该记录没有照片，无法 AI 识别')
      return
    }
    if (!aiVisionEnabled) {
      toast.show('视觉模型未配置')
      return
    }
    setAiRunning(true)
    try {
      const res = await api.aiImage(record.id)
      if (res.result && typeof res.result === 'object') {
        setAiTags(res.result as AiTags)
      }
      toast.show(`AI 识别完成（今日剩余 ${res.quota.remaining} 次）`)
    } catch (e) {
      toast.show(e instanceof Error ? e.message : 'AI 失败')
    } finally {
      setAiRunning(false)
    }
  }

  async function remove() {
    if (!confirm('确定删除这条记录？照片也会一并删除。')) return
    try {
      await api.deleteRecord(record.id)
      onDeleted()
      onClose()
    } catch (e) {
      toast.show(e instanceof Error ? e.message : '删除失败')
    }
  }

  return (
    <div className="overlay" onClick={onClose}>
      <div className="overlay-content" onClick={(e) => e.stopPropagation()}>
        <div className="overlay-header">
          <h2>
            {icon.emoji} {icon.label}
          </h2>
          <button className="close-btn" onClick={onClose} aria-label="关闭">
            ×
          </button>
        </div>

        <div className="detail-time">{formatTime(record.occurred_at)}</div>

        {record.photo_id && (
          <div className="detail-photo">
            <img
              src={stablePhotoUrl(record.photo_id, record.photo_signed_url) || ''}
              alt="记录照片"
              loading="lazy"
              decoding="async"
            />
          </div>
        )}

        {(record.color || record.texture) && (
          <div className="detail-tags">
            {record.color && (() => {
              const c = DIAPER_COLORS.find((x) => x.value === record.color)
              return c ? (
                <div className="detail-tag-row">
                  <span className="detail-tag-label">颜色</span>
                  <span className="detail-tag-value">
                    <span className="detail-tag-dot" style={{ background: c.cssColor }} />
                    {c.label}
                  </span>
                </div>
              ) : null
            })()}
            {record.texture && (() => {
              const t = DIAPER_TEXTURES.find((x) => x.value === record.texture)
              return t ? (
                <div className="detail-tag-row">
                  <span className="detail-tag-label">形状</span>
                  <span className="detail-tag-value">
                    <span className="detail-tag-emoji">{t.emoji}</span>
                    {t.label}
                  </span>
                </div>
              ) : null
            })()}
          </div>
        )}

        {record.note && (
          <div className="detail-note">
            <strong>备注：</strong>
            {record.note}
          </div>
        )}

        {aiTags && (
          <div className="ai-tags">
            <h3>AI 辅助观察</h3>
            {aiTags.color && (
              <div className="ai-tag-row">
                <span className="ai-tag-label">颜色</span>
                <span className="ai-tag-value">{aiTags.color}</span>
              </div>
            )}
            {aiTags.texture && (
              <div className="ai-tag-row">
                <span className="ai-tag-label">形态</span>
                <span className="ai-tag-value">{aiTags.texture}</span>
              </div>
            )}
            {aiTags.tags && aiTags.tags.length > 0 && (
              <div className="ai-tag-row">
                <span className="ai-tag-label">标签</span>
                <div className="ai-tag-chips">
                  {aiTags.tags.map((t, i) => (
                    <span key={i} className="ai-chip">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {aiTags.summary && <p className="ai-summary">{aiTags.summary}</p>}
            <p className="ai-disclaimer">⚠️ 非医学诊断，请咨询专业医生</p>
          </div>
        )}

        <div className="detail-actions">
          {record.photo_id && aiVisionEnabled && !aiTags && (
            <button className="btn-primary" onClick={runAi} disabled={aiRunning}>
              {aiRunning ? 'AI 分析中...' : '🤖 AI 辅助标签'}
            </button>
          )}
          <button className="text-danger" onClick={remove}>
            删除记录
          </button>
        </div>
      </div>
    </div>
  )
}
